import { z } from 'zod'

const provinceSchema = z.object({
  name: z.string(),
  slug: z.string(),
})

const villageSchema = z.object({
  name: z.string(),
  slug: z.string(),
})

const capacitySchema = z.object({
  min: z.number().int(),
  max: z.number().int(),
})

const mediaSchema = z.object({
  numPhotos: z.number().int(),
  numVideos: z.number().int(),
  thumb: z.string(),
})

const priceSchema = z
  .object({
    amount: z.number().int(),
    currency: z.string(),
  })
  .nullable()

const averagePriceSchema = z
  .object({
    amount: z.number().int(),
    currency: z.string(),
  })
  .nullable()

const reviewSchema = z.object({
  numReviews: z.number().int(),
  dataAverageReview: z.string().nullable(),
  score: z.number(),
})

const cottageSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  province: provinceSchema,
  village: villageSchema,
  regionSlug: z.string(),
  address: z.string(),
  distanceInKm: z.number().nullable(),
  latitude: z.string(),
  longitude: z.string(),
  description: z.string(),
  rentType: z.enum(['ROOMS', 'FULL', 'BOTH']),
  numBeds: z.number().int(),
  numRooms: z.number().int(),
  capacity: capacitySchema,
  media: mediaSchema,
  price: priceSchema,
  priceType: z.enum(['exact', 'minimum']).nullable(),
  averagePrice: averagePriceSchema,
  review: reviewSchema,
  hasOnlineBooking: z.boolean(),
  hasFreeCancellations: z.boolean(),
  isHighlight: z.boolean(),
})

const filterSuggestionSchema = z.object({
  filterId: z.number().int(),
  name: z.string(),
  cottagesNum: z.number().int(),
})

const locationSchema = z.object({
  name: z.string(),
  slug: z.string(),
})

const breadCrumbsSchema = z.object({
  region: locationSchema.nullable(),
  province: locationSchema.nullable(),
  village: locationSchema.nullable(),
  zone: locationSchema.nullable(),
  corner: locationSchema.nullable(),
})

export const schema = z.object({
  cottages: z.array(cottageSchema),
  totalCottages: z.number().int(),
  nearCottages: z.array(cottageSchema),
  nearCottagesTotal: z.number().int(),
  filterSuggestionsToRemove: z.array(filterSuggestionSchema),
  showOnlineBookingAlert: z.boolean(),
  showAddDatesAlert: z.boolean(),
  showFewResultsAlert: z.boolean(),
  showExactPlacesAlert: z.boolean(),
  breadCrumbs: breadCrumbsSchema,
  corner: z
    .object({
      id: z.number().int(),
      name: z.string(),
      slug: z.string(),
      image: z.string(),
    })
    .nullable(),

  destination: z
    .object({
      id: z.number().int(),
      name: z.string(),
      slug: z.string(),
      image: z.string(),
      type: z.enum(['region', 'province', 'village', 'zone']),
      parentName: z.string(),
      parentSlug: z.string(),
      parentType: z.string(),
    })
    .nullable(),
})

export type schemaType = z.infer<typeof schema>
export type CottageType = z.infer<typeof cottageSchema>
export type ProvinceType = z.infer<typeof provinceSchema>
export type VillageType = z.infer<typeof villageSchema>
export type CapacityType = z.infer<typeof capacitySchema>
export type MediaType = z.infer<typeof mediaSchema>
export type PriceType = z.infer<typeof priceSchema>
export type ReviewType = z.infer<typeof reviewSchema>
export type FilterSuggestionType = z.infer<typeof filterSuggestionSchema>
export type LocationType = z.infer<typeof locationSchema>
export type BreadCrumbsType = z.infer<typeof breadCrumbsSchema>
